import { SNSPreviewLiveRender } from "../../../preview-live/sns-preview-live-render";
import { SupportedPreviewPlatform } from "../../../core/supported-preview-platform";
import { Roles } from "../../../../constants/roles";
import { Patterns } from "../../../../constants/patterns";
import { CommonUtils } from "../../../../util/common-utils";
import { snsPreviewManagerForm } from "../../sns-preview-manager-form";
import { OgpRender } from "../../../../util/ogp/ogp-render";

const ogpRender = new OgpRender();

export class CardPreviewTileRender {
    private static appendPlatformBadge(tile: HTMLElement): void {
        snsPreviewManagerForm.getPlatformBadges(tile).forEach((element) => element.remove());

        const platform = tile.getAttribute("data-sns-platform") || "";
        const platformConfig = Patterns.SNS_PLATFORMS[platform];
        if (!platformConfig) return;

        const badge = document.createElement("div");
        badge.className = "sns-preview-tile__platform-badge";
        badge.setAttribute("aria-label", platformConfig.name);
        badge.title = platformConfig.name;

        const iconElement = document.createElement("i");
        iconElement.className = `bi ${platformConfig.icon} sns-icon`;
        iconElement.style.color = platformConfig.color;
        iconElement.setAttribute("aria-hidden", "true");

        badge.appendChild(iconElement);
        tile.appendChild(badge);
    }

    private static createTempContainer(): HTMLDivElement | null {
        const fragment = document.createRange().createContextualFragment("<div></div>");
        const container = fragment.firstElementChild;
        return container instanceof HTMLDivElement ? container : null;
    }

    private static appendOpenOverlay(tile: HTMLElement, rawUrl: string): void {
        const safeUrl = CommonUtils.escapeHtmlAttribute(String(rawUrl || ""));

        tile.insertAdjacentHTML(
            "beforeend",
            `<a class="sns-preview-open" data-role="${CommonUtils.escapeHtmlAttribute(Roles.SNS_OPEN)}" href="${safeUrl}" target="_blank" rel="noopener noreferrer" aria-label="Open SNS Link"></a>`,
        );
    }

    public static shouldDisableExternalOpen(boardMediaArea: HTMLElement): boolean {
        return snsPreviewManagerForm.getClosestPostCard(boardMediaArea) instanceof HTMLElement;
    }

    public static disableExternalOpen(tile: HTMLElement): void {
        tile.setAttribute("data-sns-open-disabled", "1");
        snsPreviewManagerForm.getSnsOpenElements(tile).forEach((el) => {
            if (!(el instanceof HTMLElement)) return;
            el.removeAttribute("href");
            el.removeAttribute("target");
            el.removeAttribute("rel");
            el.setAttribute("aria-disabled", "true");
            el.setAttribute("tabindex", "-1");
        });
    }

    private static createRenderedTile(boardMediaArea: HTMLElement, rawUrl: string): HTMLElement | null {
        const tmp = this.createTempContainer();
        if (!(tmp instanceof HTMLDivElement)) return null;

        SNSPreviewLiveRender.renderSnsPreview(rawUrl, tmp, { includeRemoveControls: false });

        const tile = tmp.firstElementChild;
        if (!(tile instanceof HTMLElement)) return null;

        const disableExternalOpen = this.shouldDisableExternalOpen(boardMediaArea);

        const controls = snsPreviewManagerForm.getMediaControls(tile);
        if (controls) controls.remove();

        const hasOpenOverlay = snsPreviewManagerForm.getSnsOpenAnchor(tile) instanceof HTMLElement;
        if (!hasOpenOverlay && !disableExternalOpen) {
            this.appendOpenOverlay(tile, rawUrl);
        }

        tile.classList.add("sns-preview-tile--index");
        tile.setAttribute("data-sns-url", rawUrl);

        const platform = this.getImageFirstPlatform(rawUrl);
        if (platform && !tile.getAttribute("data-sns-platform")) {
            tile.setAttribute("data-sns-platform", platform);
        }

        if (disableExternalOpen) {
            this.disableExternalOpen(tile);
        }

        const openOverlay = snsPreviewManagerForm.getSnsOpenAnchor(tile);
        if (
            openOverlay instanceof HTMLAnchorElement &&
            !disableExternalOpen &&
            !openOverlay.getAttribute("data-sns-url")
        ) {
            openOverlay.setAttribute("data-sns-url", rawUrl);
        }

        if (!tile.getAttribute("data-role")) {
            tile.setAttribute("data-role", Roles.SNS_PREVIEW_TILE);
        }

        this.appendPlatformBadge(tile);

        return tile;
    }

    private static createInstagramImageTile(boardMediaArea: HTMLElement, rawUrl: string): HTMLElement {
        const platform = this.getImageFirstPlatform(rawUrl) || "instagram";
        const tile = document.createElement("div");
        tile.className = "sns-preview-tile sns-preview-tile--index";
        tile.setAttribute("data-role", Roles.SNS_PREVIEW_TILE);
        tile.setAttribute("data-sns-url", rawUrl);
        tile.setAttribute("data-sns-platform", platform);

        const disableExternalOpen = this.shouldDisableExternalOpen(boardMediaArea);
        if (!disableExternalOpen) {
            this.appendOpenOverlay(tile, rawUrl);
            const openOverlay = snsPreviewManagerForm.getSnsOpenAnchor(tile);
            if (openOverlay instanceof HTMLAnchorElement) {
                openOverlay.setAttribute("data-sns-url", rawUrl);
            }
        } else {
            this.disableExternalOpen(tile);
        }

        this.appendPlatformBadge(tile);

        return tile;
    }

    private static getImageFirstPlatform(rawUrl: string): SupportedPreviewPlatform | null {
        const platform = SNSPreviewLiveRender.getSupportedPreviewPlatform(rawUrl);
        if (!platform) {
            return null;
        }

        return platform;
    }

    private static getOgpImageFromMap(el: HTMLElement, rawUrl: string): string | null {
        // data-ogp-image-map sits on the board-media-area ancestor, not the grid passed as el.
        // Walk up the DOM until we find it.
        let current: HTMLElement | null = el;
        while (current) {
            const mapJson = current.dataset.ogpImageMap;
            if (mapJson) {
                try {
                    const map = JSON.parse(mapJson) as Record<string, string>;
                    const path = typeof map[rawUrl] === "string" ? String(map[rawUrl]).trim() : "";
                    return path || null;
                } catch {
                    return null;
                }
            }
            current = current.parentElement;
        }
        return null;
    }

    private static async hydrateInstagramImageTile(
        boardMediaArea: HTMLElement,
        tile: HTMLElement,
        rawUrl: string,
    ): Promise<void> {
        const imageFirstPlatform = this.getImageFirstPlatform(rawUrl);
        const fetchedUrl = await ogpRender.fetchPreviewImageUrl(rawUrl);
        const previewImageUrl = fetchedUrl || this.getOgpImageFromMap(boardMediaArea, rawUrl);
        if (!tile.isConnected) return;

        if (previewImageUrl) {
            const img = document.createElement("img");
            img.className = "sns-preview-thumb";
            img.setAttribute("data-role", Roles.SNS_PREVIEW_THUMB);
            img.alt = `${
                imageFirstPlatform === "tiktok"
                    ? "TikTok"
                    : imageFirstPlatform === "youtube"
                      ? "YouTube"
                      : imageFirstPlatform === "facebook"
                        ? "Facebook"
                        : imageFirstPlatform === "x"
                          ? "X"
                          : "Instagram"
            } preview`;
            img.loading = "lazy";
            img.src = previewImageUrl;
            tile.replaceChildren(img);

            const disableExternalOpen = this.shouldDisableExternalOpen(boardMediaArea);
            if (!disableExternalOpen) {
                this.appendOpenOverlay(tile, rawUrl);
                const openOverlay = snsPreviewManagerForm.getSnsOpenAnchor(tile);
                if (openOverlay instanceof HTMLAnchorElement) {
                    openOverlay.setAttribute("data-sns-url", rawUrl);
                }
            } else {
                this.disableExternalOpen(tile);
            }

            this.appendPlatformBadge(tile);
            return;
        }

        if (this.shouldDisableExternalOpen(boardMediaArea)) {
            // No OGP image available and external open is disabled (inside a post card).
            // Mark the tile so CSS can center the platform badge instead of leaving it blank.
            tile.setAttribute("data-preview-state", "no-image");
            this.appendPlatformBadge(tile);
            return;
        }

        const fallbackTile = this.createRenderedTile(boardMediaArea, rawUrl);
        if (fallbackTile) {
            tile.replaceWith(fallbackTile);
        }
    }

    public static renderIndexSnsTilePreview(boardMediaArea: Element, url: string): void {
        if (!(boardMediaArea instanceof HTMLElement)) return;

        const rawUrl = String(url || "").trim();
        if (!rawUrl) return;

        if (this.getImageFirstPlatform(rawUrl)) {
            const tile = this.createInstagramImageTile(boardMediaArea, rawUrl);
            boardMediaArea.appendChild(tile);
            void this.hydrateInstagramImageTile(boardMediaArea, tile, rawUrl);
            return;
        }

        const tile = this.createRenderedTile(boardMediaArea, rawUrl);
        if (!tile) return;

        boardMediaArea.appendChild(tile);
    }
}
